import { SupportedLeadCardType } from 'cards/types/supportedLeadCardTypes';
import type { RequiredSEOData } from '../types';

export type TemplateProps = {
  filterKey: string;
  urlSuffix: string;
  labelTitleCase: string;
  labelLowerCase: string;
  labelCapitalized: string;
};

type Template = (props: TemplateProps) => string;

type Templates = {
  [key in keyof RequiredSEOData]: Template;
};

type CardTypeDefault = {
  unfiltered: RequiredSEOData; // default data is used for non-filtered cards
  templates?: Templates; // templates are used when a filter (e.g. "rustic" or "gold") is selected
};

export const REQUIRED_DATA_BY_CARD_TYPE: Record<
  SupportedLeadCardType,
  CardTypeDefault | undefined
> = {
  INVITATION: {
    unfiltered: {
      canonical: 'https://www.zola.com/wedding-planning/invitations/shop',
      title: 'Wedding Invitations',
      h1: 'Wedding invitations',
      h2: 'All about wedding invites',
      faqHeading: 'Frequently asked questions',
      description:
        'Find and design stunning and affordable wedding invitations! Explore a plethora of curated styles, colors, and designs or create your own invitations with ease. Free samples & envelopes!',
    },
    templates: {
      canonical: p => `https://www.zola.com/wedding-planning/invitations/shop${p.urlSuffix}`,
      title: p => `${p.labelTitleCase} Wedding Invitations`,
      h1: p => `${p.labelCapitalized} wedding invitations`,
      h2: p => `All about ${p.labelLowerCase} wedding invites`,
      faqHeading: () => 'Frequently asked questions',
      description: p =>
        `Find and design stunning and affordable ${p.labelLowerCase} wedding invitations! Explore a plethora of curated styles, colors, and designs or create your own invitations with ease. Free samples & envelopes!`,
    },
  },
  SAVE_THE_DATE: {
    unfiltered: {
      canonical: 'https://www.zola.com/wedding-planning/save-the-date/shop',
      title: 'Save the Date Cards for Weddings',
      h1: 'Save the date cards',
      h2: 'Announce your special day with style',
      faqHeading: 'All about save the date cards, postcards, & magnets',
      description:
        'Discover high-quality and affordable save the date cards. Choose from curated styles or create your own, ensuring your special day is announced with both elegance and ease.',
    },
    templates: {
      canonical: p => `https://www.zola.com/wedding-planning/save-the-date/shop${p.urlSuffix}`,
      title: p => `${p.labelTitleCase} Save the Date Cards for Weddings`,
      h1: p => `${p.labelCapitalized} save the date cards`,
      h2: p => `All about ${p.labelLowerCase} save the dates`,
      faqHeading: () => 'Frequently asked questions',
      description: p =>
        `Discover high-quality and affordable ${p.labelLowerCase} save the date cards. Choose from curated styles or create your own, ensuring your special day is announced with both elegance and ease.`,
    },
  },
  DIGITAL_SAVE_THE_DATE: {
    unfiltered: {
      canonical: 'https://www.zola.com/wedding-planning/digital/save-the-date/shop',
      title: 'Free Digital Save the Dates for Weddings',
      h1: 'Digital save the dates',
      h2: 'A free way to announce your special day',
      faqHeading: 'All about digital save the dates',
      description:
        "Spread the word about your wedding by text, email, or DM with digital save the date cards. Customize a beautiful design in minutes. The best part? They're free.",
    },
    templates: {
      canonical: p =>
        `https://www.zola.com/wedding-planning/digital/save-the-date/shop${p.urlSuffix}`,
      title: p => `Free ${p.labelTitleCase} Digital Save The Dates`,
      h1: p => `${p.labelCapitalized} digital save the dates`,
      h2: p => `All about ${p.labelCapitalized} digital save the dates`,
      faqHeading: p => `FAQs - ${p.labelCapitalized} digital save the dates`,
      description: p =>
        `Spread the word about your wedding by text, email, or DM with ${p.labelLowerCase} digital save the date cards. Customize a beautiful design in minutes. The best part? They're free.`,
    },
  },
  THANK_YOU: {
    unfiltered: {
      canonical: 'https://www.zola.com/wedding-planning/thank-you-cards/shop',
      title: 'Wedding Thank You Cards',
      h1: 'Wedding thank you cards',
      h2: 'Thank yous that match your wedding style',
      faqHeading: 'All about thank you cards',
      description:
        'Easily design, create, and send thank you cards that match your wedding style. Enjoy the simplicity of gratitude with free envelopes included.',
    },
    templates: {
      canonical: p => `https://www.zola.com/wedding-planning/thank-you-cards/shop${p.urlSuffix}`,
      title: p => `${p.labelTitleCase} Thank You Cards`,
      h1: p => `${p.labelCapitalized} thank you cards`,
      h2: p => `All about ${p.labelLowerCase} thank yous`,
      faqHeading: p => `FAQs - ${p.labelCapitalized} thank you cards`,
      description: p =>
        `Easily design, create, and send ${p.labelLowerCase} thank you cards that match your wedding style. Enjoy the simplicity of gratitude with free envelopes included.`,
    },
  },
  MENU: {
    unfiltered: {
      canonical: 'https://www.zola.com/wedding-planning/menus/shop',
      title: 'Wedding Menus',
      h1: 'Wedding menus',
      h2: 'A tasteful touch for any celebration',
      faqHeading: 'All about wedding menus',
      description:
        'Discover wedding menus that effortlessly match your wedding look. Share your delicious offerings in style, adding a tasteful touch to your celebration.',
    },
    templates: {
      canonical: p => `https://www.zola.com/wedding-planning/menus/shop${p.urlSuffix}`,
      title: p => `${p.labelTitleCase} Wedding Menus`,
      h1: p => `${p.labelCapitalized} wedding menus`,
      h2: p => `All about ${p.labelLowerCase} wedding menus`,
      faqHeading: p => `FAQs - ${p.labelCapitalized} menus`,
      description: p =>
        `Discover ${p.labelLowerCase} wedding menus that effortlessly match your wedding look. Share your delicious offerings in style, adding a tasteful touch to your celebration.`,
    },
  },
  PROGRAM: {
    unfiltered: {
      canonical: 'https://www.zola.com/wedding-planning/programs/shop',
      title: 'Wedding Programs',
      h1: 'Wedding programs',
      h2: 'Programs you can cherish forever',
      faqHeading: 'All about wedding programs',
      description:
        'Complete your celebration with our wedding ceremony programs. Discover a variety of designs to complement your style. Craft every detail with elegance and ease.',
    },
    templates: {
      canonical: p => `https://www.zola.com/wedding-planning/programs/shop${p.urlSuffix}`,
      title: p => `${p.labelTitleCase} Wedding Programs`,
      h1: p => `${p.labelCapitalized} wedding programs`,
      h2: p => `All about ${p.labelLowerCase} wedding programs`,
      faqHeading: p => `FAQs - ${p.labelCapitalized} programs`,
      description: p =>
        `Complete your celebration with our wedding ceremony programs. Discover a variety of ${p.labelLowerCase} designs to complement your style. Craft every detail with elegance and ease.`,
    },
  },
  NAPKIN: {
    unfiltered: {
      canonical: 'https://www.zola.com/wedding-planning/napkins/shop',
      title: 'Wedding Napkins',
      h1: 'Wedding napkins',
      h2: 'All about our wedding napkins',
      faqHeading: 'FAQs - Wedding napkins',
      description:
        'Custom foil napkins bring a personal, stylish touch to your celebration with beautiful designs and colors to match your style.',
    },
    templates: {
      canonical: p => `https://www.zola.com/wedding-planning/napkins/shop${p.urlSuffix}`,
      title: p => `${p.labelTitleCase} Wedding Napkins`,
      h1: p => `${p.labelCapitalized} wedding napkins`,
      h2: p => `All about our ${p.labelLowerCase} napkins`,
      faqHeading: p => `FAQs - ${p.labelCapitalized} napkins`,
      description: p =>
        `Custom foil napkins bring a personal, stylish touch to your celebration with beautiful ${p.labelLowerCase} designs.`,
    },
  },
  // TODO: hmyers - Add real signage SEO copy
  SIGNAGE: {
    unfiltered: {
      canonical: 'https://www.zola.com/wedding-planning/signage/shop',
      title: 'Signage',
      h1: 'Signage',
      h2: 'Give me a siiiiiiign',
      faqHeading: 'All about our signage',
      description: 'Signs to point here or there or anywhere',
    },
    templates: {
      canonical: p => `https://www.zola.com/wedding-planning/signage/shop${p.urlSuffix}`,
      title: p => `${p.labelTitleCase} Signage`,
      h1: p => `${p.labelCapitalized} signage`,
      h2: p => `All about ${p.labelLowerCase} signage`,
      faqHeading: p => `FAQs - ${p.labelCapitalized} signage`,
      description: p =>
        `Display information in a big way. Discover a variety of ${p.labelLowerCase} sign designs.`,
    },
  },
  // TODO: hmyers - Add real sticker SEO copy
  STICKER: {
    unfiltered: {
      canonical: 'https://www.zola.com/wedding-planning/stickers/shop',
      title: 'Stickers',
      h1: 'Stickers',
      h2: 'Paper that sticks',
      faqHeading: 'All about our stickers',
      description: 'Custom stickers that look good on wedding paper AND skateboards.',
    },
    templates: {
      canonical: p => `https://www.zola.com/wedding-planning/stickers/shop${p.urlSuffix}`,
      title: p => `${p.labelTitleCase} Stickers`,
      h1: p => `${p.labelCapitalized} stickers`,
      h2: p => `All about ${p.labelLowerCase} stickers`,
      faqHeading: p => `FAQs - ${p.labelCapitalized} stickers`,
      description: p =>
        `Customize your wedding paper or your partner's nose with our variety of ${p.labelLowerCase} sticker designs.`,
    },
  },
  ENCLOSURE: undefined, // On the PLP, the type will always be DETAILS
  DETAILS: {
    unfiltered: {
      canonical: 'https://www.zola.com/wedding-planning/enclosures/shop',
      title: 'Details Cards for Wedding Invitations',
      h1: 'Wedding details cards',
      h2: 'All about enclosure cards',
      faqHeading: 'Frequently asked questions',
      description:
        'Complete your wedding invitation presentation with our details cards. Package all the details in style, creating a stunning ensemble to set the perfect tone for your celebration.',
    },
    templates: {
      canonical: p => `https://www.zola.com/wedding-planning/enclosures/shop${p.urlSuffix}`,
      title: p => `${p.labelTitleCase} Details Cards for Wedding Invitations`,
      h1: p => `${p.labelCapitalized} wedding details cards`,
      h2: p => `All about ${p.labelLowerCase} enclosure cards`,
      faqHeading: p => `FAQs - ${p.labelCapitalized} details cards`,
      description: p =>
        `Complete your wedding invitation presentation with our ${p.labelLowerCase} details cards. Package all the details in style, creating a stunning ensemble to set the perfect tone for your celebration.`,
    },
  },
  PLACE: {
    unfiltered: {
      canonical: 'https://www.zola.com/wedding-planning/place-cards/shop',
      title: 'Wedding Place Cards',
      h1: 'Wedding place cards',
      h2: 'Make every guest feel special with personalized place cards',
      faqHeading: 'All about place cards',
      description:
        'Explore our collection of place cards for weddings and special events. Elevate the seating experience, ensuring every detail reflects the charm of your celebration.',
    },
    templates: {
      canonical: p => `https://www.zola.com/wedding-planning/place-cards/shop${p.urlSuffix}`,
      title: p => `${p.labelTitleCase} Wedding Place Cards`,
      h1: p => `${p.labelCapitalized} place cards`,
      h2: p => `All about ${p.labelLowerCase} place cards`,
      faqHeading: p => `FAQs - ${p.labelCapitalized} place cards`,
      description: p =>
        `Explore our collection of ${p.labelLowerCase} place cards for weddings and special events. Elevate the seating experience, ensuring every detail reflects the charm of your celebration.`,
    },
  },
  CHANGE_THE_DATE: {
    unfiltered: {
      canonical: 'https://www.zola.com/wedding-planning/change-the-date/shop',
      title: 'Change The Date Cards - Zola',
      h1: 'Change the dates',
      h2: 'All about change the date cards',
      faqHeading: 'Frequently asked questions',
      description:
        'Need to postpone or reschedule your big day? Update guests with a change the date wedding card.',
    },
    templates: {
      canonical: p => `https://www.zola.com/wedding-planning/change-the-date/shop${p.urlSuffix}`,
      title: p => `${p.labelTitleCase} Change The Date Cards - Zola`,
      h1: p => `${p.labelCapitalized} change the dates`,
      h2: p => `All about ${p.labelLowerCase} change the dates`,
      faqHeading: p => `FAQs - ${p.labelCapitalized} change the date cards`,
      description: p =>
        `Need to postpone or reschedule your big day? Update guests with a ${p.labelLowerCase} change the date wedding card.`,
    },
  },
  HOLIDAY: {
    unfiltered: {
      canonical: 'https://www.zola.com/wedding-planning/holiday-cards/shop',
      title: 'Holiday Cards',
      h1: 'Holiday cards',
      h2: 'Find the perfect holiday card at Zola',
      faqHeading: 'Frequently asked questions',
      description:
        'Mark every holiday together with joy! Discover personalized holiday cards, capturing the magic of your journey. Share warmth and style.',
    },
    templates: {
      canonical: p => `https://www.zola.com/wedding-planning/holiday-cards/shop${p.urlSuffix}`,
      title: p => `${p.labelTitleCase} Cards`,
      h1: p => `${p.labelCapitalized} cards`,
      h2: p => `Find the perfect ${p.labelCapitalized} card at Zola`,
      faqHeading: () => 'Frequently asked questions',
      description: p =>
        `Mark every ${p.labelCapitalized} together with joy! Discover personalized ${p.labelCapitalized} cards, capturing the magic of your journey. Share warmth and style.`,
    },
  },
  WEDDING_SHOWER_INVITATION: {
    unfiltered: {
      canonical: 'https://www.zola.com/wedding-planning/bridal-shower/shop',
      title: 'Wedding & Bridal Shower Invitations',
      h1: 'Wedding and bridal shower invitations',
      h2: 'All about wedding shower invites',
      faqHeading: 'Frequently asked questions',
      description:
        'Find beautiful and affordable wedding shower invitations to match your wedding style. Explore our curated collection of styles or design your own invitations. Order free samples!',
    },
    templates: {
      canonical: p => `https://www.zola.com/wedding-planning/bridal-shower/shop${p.urlSuffix}`,
      title: p => `${p.labelTitleCase} Wedding & Bridal Shower Invitations`,
      h1: p => `${p.labelCapitalized} wedding shower invitations`,
      h2: p => `All about ${p.labelLowerCase} wedding shower invites`,
      faqHeading: p => `FAQs - ${p.labelCapitalized} wedding shower invitations`,
      description: p =>
        `Find beautiful and affordable ${p.labelLowerCase} wedding shower invitations to match your wedding style. Explore our curated collection of styles or design your own invitations. Order free samples!`,
    },
  },
  TABLE_NUMBER: {
    unfiltered: {
      canonical: 'https://www.zola.com/wedding-planning/table-numbers/shop',
      title: 'Wedding Table Numbers',
      h1: 'Wedding table numbers',
      h2: 'Effortlessly guide your guests with table numbers',
      faqHeading: 'All about wedding table numbers',
      description:
        'Enhance your wedding reception with beautifully designed table numbers that perfectly complement your theme and style.',
    },
    templates: {
      canonical: p => `https://www.zola.com/wedding-planning/table-numbers/shop${p.urlSuffix}`,
      title: p => `${p.labelTitleCase} Wedding Table Numbers`,
      h1: p => `${p.labelCapitalized} table numbers`,
      h2: p => `All about ${p.labelLowerCase} table numbers`,
      faqHeading: p => `FAQs - ${p.labelCapitalized} table numbers`,
      description: p =>
        `Enhance your wedding reception with ${p.labelLowerCase} table numbers that perfectly complement your theme and style.`,
    },
  },

  MATCHING_SUITES: {
    unfiltered: {
      canonical: 'https://www.zola.com/wedding-planning/suites/shop',
      title: 'Wedding Invitation Suites',
      h1: 'Wedding invitation suites',
      h2: 'At Zola, it’s easy to create your suite',
      faqHeading: 'All about wedding invitation suites',
      description:
        'Explore complete wedding invitation suites — everything you need in one package. From invitations to RSVP cards, create a cohesive, stunning, and affordable ensemble for your special day.',
    },
    templates: {
      canonical: p => `https://www.zola.com/wedding-planning/suites/shop${p.urlSuffix}`,
      title: p => `${p.labelTitleCase} Wedding Invitation Suites`,
      h1: p => `${p.labelCapitalized} invitation suites`,
      h2: p => `Explore complete ${p.labelLowerCase} suites`,
      faqHeading: p => `All about ${p.labelLowerCase} wedding suites`,
      description: p =>
        `Live the suite life, AKA matching everything. ${p.labelCapitalized} wedding invitation suites are a great way to express your style — create your own with everything from custom save the dates and RSVP cards to menus and programs.`,
    },
  },
  BELLY_BAND: undefined, // On the PLP, the type will always be PAPER_ADD_ONS
  VELLUM_JACKET: undefined, // On the PLP, the type will always be PAPER_ADD_ONS
  WAX_SEAL: undefined, // On the PLP, the type will always be PAPER_ADD_ONS
  PAPER_ADD_ONS: {
    unfiltered: {
      canonical: 'https://www.zola.com/wedding-planning/paper-add-ons/shop',
      title: 'Paper Add-ons - Zola',
      h1: 'Paper add-ons',
      h2: 'All about paper add-ons',
      faqHeading: 'Frequently asked questions',
      description:
        'These are the finishing touches that will really impress your guests. Discover our wax seals, vellum jackets, and belly bands.',
    },
  },
};
